import { css } from 'emotion';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';

import analytics from 'site-react/core/Analytics';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerPage,
  DrawerPageGroup,
  DrawerTitle,
} from 'site-react/core/Drawer';
import theme from 'site-react/theme';

// Data
import mainNavLinks from '../data/mobileMain';

import MobileNavGuides from './MobileNavGuides';
import { MobileNavIconBack, MobileNavIconClose } from './MobileNavIcons';
import MobileNavLocations from './MobileNavLocations';
import MobileNavMenu from './MobileNavMenu';

const mobileNavStyles = css`
  @media (min-width: ${theme.breakpoints.lg}px) {
    display: none;
  }
`;

const leftAlignTitle = css`
  position: static;
  text-align: left;
  top: inherit;
  width: auto;
`;

const centerAlignTitle = css`
  pointer-events: none;
  position: relative;
  text-align: center;
  top: -21px;
  width: 100%;
`;

const MobileNav = ({ closeFunc, isVisible, isLoggedIn, logoutFunc }) => {
  const [activePanel, setActivePanel] = useState('main');

  const analyticsData = {
    location: 'mobile-meganav',
  };

  /**
   * Handle nav list item clicks
   * @param  {Object} event The click event object
   * @param  {Object} item Item clicked on
   */
  const onNavListItemClick = (event, item) => {
    event.preventDefault();

    const { clickAction = null } = item;

    analyticsHandler(item);

    // If no onClick handler has been specified.
    if (!clickAction) {
      window.location = `${item.url}`;
    }

    switch (clickAction) {
      case 'setActivePanel':
        setActivePanel(item.panelName);
        break;
      case 'logout':
        logoutFunc();
        onMobileNavClose(false);
        break;
      default:
        onMobileNavClose(false);
    }
  };

  const onMobileNavClose = (trackEvent = true) => {
    if (trackEvent) analytics.track('Mobile Nav Closed', {});

    setActivePanel('main');
    closeFunc();
  };

  /**
   * Send analytics events
   *
   * @param  {Object} item - Item clicked on
   */
  const analyticsHandler = (item) => {
    if (!item.analytics) return;
    const eventData = item.analytics.data
      ? { ...analyticsData, ...item.analytics.data(item) }
      : analyticsData;

    analytics.track(item.analytics.event, eventData);
  };

  const goToMainPanel = (eventData) => {
    analytics.track('Mobile Meganav Back Button Clicked', eventData);
    setActivePanel(null);
  };

  const currentLinks = useMemo(() => {
    return mainNavLinks.filter(
      (section) =>
        // sections _without_ a loggedIn property should always be displayed
        typeof section.loggedIn === 'undefined' ||
        // sections _with_ a loggedIn property should only be displayed if they
        // match the current isLoggedIn prop
        section.loggedIn === isLoggedIn,
    );
  }, [isLoggedIn]);

  return isVisible ? (
    <div className={mobileNavStyles}>
      <Drawer isVisible onClose={onMobileNavClose}>
        <DrawerPageGroup activePage={activePanel}>
          {[
            <DrawerPage key="main" pageName="main">
              <DrawerHeader>
                <DrawerTitle title="Main Menu" className={leftAlignTitle} />
                <MobileNavIconClose
                  data-testid="MobileNavIconClose-main"
                  onClick={onMobileNavClose}
                />
              </DrawerHeader>
              <DrawerContent>
                <MobileNavMenu
                  links={currentLinks}
                  onNavListItemClick={onNavListItemClick}
                />
              </DrawerContent>
            </DrawerPage>,
            <DrawerPage key="locations" pageName="locations">
              <DrawerHeader>
                <MobileNavIconBack
                  onClick={() =>
                    goToMainPanel({
                      button_location: 'mobile-meganav-locations',
                    })
                  }
                />
                <MobileNavIconClose
                  data-testid="MobileNavIconClose-locations"
                  onClick={onMobileNavClose}
                />
                <DrawerTitle className={centerAlignTitle} title="Locations" />
              </DrawerHeader>
              <DrawerContent>
                <MobileNavLocations onNavListItemClick={onNavListItemClick} />
              </DrawerContent>
            </DrawerPage>,
            <DrawerPage key="guides" pageName="guides">
              <DrawerHeader>
                <MobileNavIconBack
                  onClick={() =>
                    goToMainPanel({
                      button_location: 'mobile-meganav-guides',
                    })
                  }
                />
                <MobileNavIconClose
                  data-testid="MobileNavIconClose-guides"
                  onClick={onMobileNavClose}
                />
                <DrawerTitle className={centerAlignTitle} title="Guides" />
              </DrawerHeader>
              <DrawerContent>
                <MobileNavGuides onNavListItemClick={onNavListItemClick} />
              </DrawerContent>
            </DrawerPage>,
          ]}
        </DrawerPageGroup>
      </Drawer>
    </div>
  ) : null;
};

MobileNav.defaultProps = {
  closeFunc: () => {},
  isLoggedIn: false,
  isVisible: false,
  logoutFunc: null,
};

MobileNav.propTypes = {
  closeFunc: PropTypes.func,
  isLoggedIn: PropTypes.bool,
  isVisible: PropTypes.bool,
  logoutFunc: PropTypes.func,
};

export default MobileNav;
